/* =====================================================================
 * ContactSalesPage — Enterprise 문의 폼
 * ===================================================================== */

import React, { useState } from 'react';
import styled from 'styled-components';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { submitSalesContactInquiry } from '@auw/api-client';
import { usePageBack } from './usePageBack';
import { useChatStore } from '@auw/stores';
import { Button } from '@auw/workspace-ui';
import { Input } from '@auw/workspace-ui';
import { Textarea } from '@auw/workspace-ui';

export const ContactSalesPage: React.FC = () => {
  const { t } = useTranslation('widget');
  // Back returns to the page that opened this one (landing, pricing, settings), not a fixed route.
  const handleBack = usePageBack('/');
  const addToast = useChatStore((s) => s.addToast);

  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      addToast({ type: 'warning', title: t('contactSales.nameEmailRequired') });
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      addToast({ type: 'warning', title: t('contactSales.emailInvalid') });
      return;
    }

    setSubmitting(true);
    try {
      await submitSalesContactInquiry({
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim(),
        message: form.message.trim(),
      });
      addToast({
        type: 'success',
        title: t('contactSales.inquirySent'),
        description: t('contactSales.replyWithinDays'),
      });
      setForm({ name: '', email: '', company: '', message: '' });
    } catch {
      addToast({
        type: 'error',
        title: t('contactSales.submitFail'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Wrap>
      <TopBar>
        <Button variant="ghost" size="sm" leftIcon={<ArrowLeft size={16} />} onClick={handleBack} aria-label={t('contactSales.back')}>
          {t('contactSales.back')}
        </Button>
        <TopBarTitle>{t('contactSales.topBarTitle')}</TopBarTitle>
      </TopBar>

      <Card>
        <Title>{t('contactSales.title')}</Title>
        <Desc>
          {t('contactSales.description')}
        </Desc>

        <Form onSubmit={handleSubmit}>
          <FieldGroup>
            <Label htmlFor="cs-name">{t('contactSales.nameLabel')}</Label>
            <Input
              id="cs-name"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder={t('contactSales.namePlaceholder')}
              required
            />
          </FieldGroup>

          <FieldGroup>
            <Label htmlFor="cs-email">{t('contactSales.emailLabel')}</Label>
            <Input
              id="cs-email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              placeholder={t('contactSales.emailPlaceholder')}
              required
            />
          </FieldGroup>

          <FieldGroup>
            <Label htmlFor="cs-company">{t('contactSales.companyLabel')}</Label>
            <Input
              id="cs-company"
              name="company"
              value={form.company}
              onChange={handleChange}
              placeholder={t('contactSales.companyPlaceholder')}
            />
          </FieldGroup>

          <FieldGroup>
            <Label htmlFor="cs-message">{t('contactSales.messageLabel')}</Label>
            <Textarea
              id="cs-message"
              name="message"
              value={form.message}
              onChange={handleChange}
              placeholder={t('contactSales.messagePlaceholder')}
              minRows={5}
            />
          </FieldGroup>

          <SubmitBtn type="submit" disabled={submitting}>
            {submitting ? t('contactSales.submitting') : t('contactSales.submit')}
          </SubmitBtn>
        </Form>
      </Card>

      <PageFooter>
        <FooterLogo src="/agent_logo.png" alt="" />
        <span>TEAMREBOOTT, Inc.</span>
      </PageFooter>
    </Wrap>
  );
};

export default ContactSalesPage;

/* ---- Styles ---- */

const Wrap = styled.div`
  height: 100vh;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
  background: ${({ theme }) => theme.colors.bgSecondary};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 16px 80px;

  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar { display: none; }
`;

const TopBar = styled.header`
  width: 100%;
  max-width: 560px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 24px 0 16px;
`;

const TopBarTitle = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const Card = styled.main`
  width: 100%;
  max-width: 560px;
  background: ${({ theme }) => theme.colors.bgPrimary};
  border-radius: ${({ theme }) => theme.sizes.borderRadiusLg};
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  box-shadow: ${({ theme }) => theme.shadows.md};
  padding: 40px 40px 44px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 28px 20px 32px;
    border-radius: ${({ theme }) => theme.sizes.borderRadius};
  }
`;

const Title = styled.h1`
  margin: 0 0 8px;
  font-size: 1.5rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  letter-spacing: -0.02em;
`;

const Desc = styled.p`
  margin: 0 0 28px;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.6;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const SubmitBtn = styled.button`
  padding: 12px 20px;
  border: none;
  border-radius: ${({ theme }) => theme.sizes.borderRadiusSm};
  background: ${({ theme }) => theme.colors.textPrimary};
  color: ${({ theme }) => theme.colors.bgPrimary};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity ${({ theme }) => theme.transitions.fast};
  margin-top: 4px;

  &:hover:not(:disabled) { opacity: 0.85; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const PageFooter = styled.footer`
  width: 100%;
  max-width: 560px;
  margin-top: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  opacity: 0.5;

  span {
    font-size: 0.6875rem;
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

const FooterLogo = styled.img`
  width: 14px;
  height: 14px;
  object-fit: contain;
`;
